import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trash2, Phone, Mail, Building2, Crown, Megaphone } from "lucide-react";
import { toast } from "sonner";

const PKG_LABEL = {
  title: "Title Sponsor (Naming Rights)",
  league: "League Sponsor",
  team: "Team Sponsor",
  banner: "Court Banner",
  stall: "Commercial Stall",
  other: "Custom",
};

const INTENT_FILTERS = [
  { key: "all",         label: "All Leads",   icon: null,      accent: "#9ca3af" },
  { key: "franchise",   label: "Franchise",   icon: Crown,     accent: "#E32424" },
  { key: "sponsorship", label: "Sponsorship", icon: Megaphone, accent: "#00A86B" },
];

export default function SponsorInquiries() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [intent, setIntent] = useState("all");

  // Fetch all inquiries once; filter client-side for instant tab switching + accurate counts.
  useEffect(() => {
    (async () => {
      const { data } = await api.get("/sponsor-inquiries");
      setItems(data);
    })();
  }, []);

  const reload = async () => {
    const { data } = await api.get("/sponsor-inquiries");
    setItems(data);
  };

  const update = async (id, payload) => {
    await api.put(`/sponsor-inquiries/${id}`, payload);
    reload();
  };
  const del = async (id) => {
    if (!window.confirm("Delete inquiry?")) return;
    await api.delete(`/sponsor-inquiries/${id}`);
    reload();
  };

  const visible = useMemo(() => {
    if (intent === "all") return items;
    if (intent === "franchise") return items.filter((i) => i.intent === "franchise");
    // legacy rows without intent → bucketed under sponsorship (default)
    return items.filter((i) => i.intent === "sponsorship" || !i.intent);
  }, [items, intent]);

  const by = (s) => visible.filter((i) => i.status === s);
  const counts = useMemo(() => ({
    all: items.length,
    franchise:   items.filter((i) => i.intent === "franchise").length,
    sponsorship: items.filter((i) => i.intent === "sponsorship" || !i.intent).length,
  }), [items]);

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Commercial Pipeline</div>
        <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white">Sponsor Leads</h1>
        <div className="text-xs text-zinc-400 mt-1">{visible.length} shown · {by("new").length} new · {by("contacted").length} contacted · {by("won").length} won</div>
      </div>

      {/* Intent filter */}
      <div className="mb-6 flex flex-wrap gap-2" data-testid="inquiry-intent-filter">
        {INTENT_FILTERS.map((f) => {
          const Icon = f.icon;
          const active = intent === f.key;
          const count = counts[f.key];
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setIntent(f.key)}
              data-testid={`inquiry-filter-${f.key}`}
              className={`px-4 py-2 rounded-lg border text-xs uppercase tracking-widest font-bold flex items-center gap-2 transition-colors ${active ? "bg-white/10 text-white" : "bg-[#11241b] text-zinc-400 hover:text-white"}`}
              style={{ borderColor: active ? f.accent : "rgba(255,255,255,0.1)" }}
            >
              {Icon && <Icon className="w-3.5 h-3.5" style={{ color: f.accent }} />}
              {f.label}
              <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: active ? f.accent : "transparent", color: active ? "white" : "rgba(255,255,255,0.5)", border: active ? "none" : "1px solid rgba(255,255,255,0.1)" }}>{count}</span>
            </button>
          );
        })}
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {["new", "contacted", "won"].map((bucket) => (
          <div key={bucket} data-testid={`bucket-${bucket}`}>
            <div className="mb-3 flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${bucket === "new" ? "bg-[#BB0000] animate-pulse" : bucket === "contacted" ? "bg-amber-500" : "bg-[#00A86B]"}`} />
              <span className="font-anton text-xl uppercase tracking-tight text-white">{bucket}</span>
              <span className="text-xs text-zinc-500">{by(bucket).length}</span>
            </div>
            <div className="space-y-2">
              {by(bucket).map((i) => (
                <Card key={i.id} className="p-3 bg-[#11241b] border-white/10" data-testid={`inquiry-${i.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-anton text-lg uppercase tracking-tight text-white truncate flex items-center gap-1">
                        <Building2 className="w-3 h-3 text-[#00A86B]" /> {i.brand}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {i.intent === "franchise" ? (
                          <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-[#E32424]/20 text-[#E32424] border border-[#E32424]/40 flex items-center gap-1" data-testid={`intent-badge-${i.id}`}>
                            <Crown className="w-2.5 h-2.5" /> Franchise
                          </span>
                        ) : (
                          <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-[#00A86B]/20 text-[#00A86B] border border-[#00A86B]/40 flex items-center gap-1" data-testid={`intent-badge-${i.id}`}>
                            <Megaphone className="w-2.5 h-2.5" /> Sponsor
                          </span>
                        )}
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{PKG_LABEL[i.package_interest] || i.package_interest}</div>
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => del(i.id)} className="text-red-400" data-testid={`del-inquiry-${i.id}`}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                  <div className="mt-2 text-xs space-y-0.5 text-zinc-300">
                    {i.contact_name && <div>{i.contact_name}</div>}
                    {i.email && <div className="flex items-center gap-1 truncate"><Mail className="w-3 h-3 text-zinc-500" />{i.email}</div>}
                    {i.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3 text-zinc-500" />{i.phone}</div>}
                    {i.message && <div className="text-zinc-400 line-clamp-3 mt-1">&quot;{i.message}&quot;</div>}
                  </div>
                  <div className="mt-3 flex gap-1">
                    {bucket === "new" && (
                      <Button size="sm" variant="outline" className="border-amber-500/40 text-amber-400 text-[10px]" onClick={() => update(i.id, { status: "contacted" })} data-testid={`move-contacted-${i.id}`}>→ Contacted</Button>
                    )}
                    {bucket === "contacted" && (
                      <>
                        <Button size="sm" variant="outline" className="border-[#00A86B]/40 text-[#00A86B] text-[10px]" onClick={() => update(i.id, { status: "won" })} data-testid={`move-won-${i.id}`}>✓ Won</Button>
                        <Button size="sm" variant="outline" className="border-zinc-500/40 text-zinc-500 text-[10px]" onClick={() => update(i.id, { status: "lost" })} data-testid={`move-lost-${i.id}`}>✗ Lost</Button>
                      </>
                    )}
                  </div>
                </Card>
              ))}
              {by(bucket).length === 0 && <div className="text-xs text-zinc-600 py-4 text-center">No leads in this bucket.</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
